/**
 * DomainSetupForm — the 2-step "add a custom domain" body, host-agnostic.
 *
 * Extracted from DomainSetupDialog (follow-up F1) so the same wizard can render
 * EITHER inside a modal (Integrations page → `DomainSetupDialog`) or directly in
 * place (launch activation domain Drawer → `CustomDomainSection addMode="inline"`),
 * without a dialog-over-drawer hop.
 *
 * Steps: 1) enter + validate the domain, 2) show the DNS records to add.
 *
 * The step's primary action is owned here but PLACED by the host:
 *   - `actionPlacement="inline"` (default) → the form renders the button itself;
 *   - `actionPlacement="host"` → the form only reports the action descriptor
 *     through `onActionChange`, and the host renders it (SDialog's footer slot).
 * Either way the button keeps the canonical test ids
 * (`CUSTOM_DOMAIN_TEST_IDS.continueBtn` / `.dnsDoneBtn`), so a spec finds it in
 * both surfaces.
 */

import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { DNSInstructions } from './DNSInstructions';
import {
  CUSTOM_DOMAIN_VALIDATION,
  ALLOWED_PLATFORM_SUBDOMAINS,
  type DomainSetupResponse,
} from '../types/customDomain.types';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';

/** Wizard steps */
const STEPS = ['Enter Domain', 'Configure DNS'];

/** The step's primary action, so a host (SDialog) can render it in its own footer. */
export interface DomainSetupFormAction {
  label: string;
  onClick: () => void;
  disabled: boolean;
  'data-testid': string;
}

export interface DomainSetupFormProps {
  /** Setup domain handler — returns the setup response with the DNS records. */
  onSetup: (domain: string) => Promise<DomainSetupResponse>;
  /** Step 2 confirmation ("I've added the DNS records"): the dialog closes, the inline host refreshes. */
  onDone: () => void;
  /** Render step-1 copy WITHOUT the workflow guide anchors (threaded from the host). */
  suppressGuideAnchors?: boolean;
  /** Changing this value resets the wizard back to step 1 (the dialog flips it on open). */
  resetToken?: unknown;
  /** Where the step action button lives (see the file header). */
  actionPlacement?: 'inline' | 'host';
  /** Called whenever the step action changes — required for `actionPlacement="host"`. */
  onActionChange?: (action: DomainSetupFormAction) => void;
  /** Called whenever a setup request starts/finishes (the dialog blocks close while in flight). */
  onSubmittingChange?: (submitting: boolean) => void;
}

/**
 * Validate the domain format. Mirrors `isValidDomain` so the UI pre-check stays
 * in step with the API.
 */
export function validateDomainInput(value: string): string | null {
  if (!value.trim()) return 'Domain is required';
  if (value.length < CUSTOM_DOMAIN_VALIDATION.MIN_LENGTH) return 'Domain is too short';
  if (value.length > CUSTOM_DOMAIN_VALIDATION.MAX_LENGTH) return 'Domain is too long';
  if (!CUSTOM_DOMAIN_VALIDATION.PATTERN.test(value)) {
    return 'Invalid domain format. Use lowercase letters, numbers, and hyphens only.';
  }
  // Subdomain required (at least 3 parts: subdomain.domain.tld)
  if (value.split('.').length < 3) {
    return 'Please enter a subdomain (e.g., promo.yourdomain.com)';
  }
  // Reserved domains — but let the branded platform subdomains through first.
  const lower = value.toLowerCase();
  const allowedPlatformSubdomains: readonly string[] = ALLOWED_PLATFORM_SUBDOMAINS;
  if (!allowedPlatformSubdomains.includes(lower)) {
    // Suffix match so any subdomain of a reserved zone (api./app./www./…) is blocked.
    const isReserved = CUSTOM_DOMAIN_VALIDATION.RESERVED_DOMAINS.some(
      (r) => lower === r || lower.endsWith(`.${r}`),
    );
    if (isReserved) return 'This domain is reserved and cannot be used';
  }
  return null;
}

export const DomainSetupForm: React.FC<DomainSetupFormProps> = ({
  onSetup,
  onDone,
  suppressGuideAnchors = false,
  resetToken,
  actionPlacement = 'inline',
  onActionChange,
  onSubmittingChange,
}) => {
  const guideService = useMemo(() => new GuideService(), []);

  const [activeStep, setActiveStep] = useState(0);
  const [domain, setDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupResponse, setSetupResponse] = useState<DomainSetupResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  /** Reset the wizard whenever the host flips the token (dialog open/close). */
  useEffect(() => {
    setActiveStep(0);
    setDomain('');
    setValidationError(null);
    setSubmitting(false);
    setSetupResponse(null);
    setApiError(null);
  }, [resetToken]);

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  const handleDomainChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(e.target.value.toLowerCase().trim());
    setValidationError((current) => (current ? null : current));
  }, []);

  /** Step 1 → Step 2 */
  const handleSubmit = useCallback(async () => {
    const error = validateDomainInput(domain);
    if (error) {
      setValidationError(error);
      return;
    }

    setSubmitting(true);
    setApiError(null);

    try {
      const response = await onSetup(domain);
      setSetupResponse(response);
      setActiveStep(1);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to setup domain. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [domain, onSetup]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && activeStep === 0 && !submitting) {
        e.preventDefault();
        void handleSubmit();
      }
    },
    [activeStep, submitting, handleSubmit],
  );

  const action: DomainSetupFormAction = useMemo(
    () =>
      activeStep === 0
        ? {
            label: submitting ? 'Setting up...' : 'Continue',
            onClick: () => void handleSubmit(),
            disabled: submitting || !domain.trim(),
            'data-testid': CUSTOM_DOMAIN_TEST_IDS.continueBtn,
          }
        : {
            label: "I've added the DNS records",
            onClick: onDone,
            disabled: false,
            'data-testid': CUSTOM_DOMAIN_TEST_IDS.dnsDoneBtn,
          },
    [activeStep, submitting, domain, handleSubmit, onDone],
  );

  // Layout effect (not useEffect): a host rendering the action in its own chrome
  // must never paint the PREVIOUS step's label for a frame after a step change.
  useLayoutEffect(() => {
    onActionChange?.(action);
  }, [action, onActionChange]);

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Enter Domain */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {suppressGuideAnchors ? (
              <>Enter your subdomain to connect it to your funnels.</>
            ) : (
              <WithGuideHelp
                guideKey={GUIDE_ANCHORS.customDomainInput.guideKey}
                guideService={guideService}
                data-testid={GUIDE_ANCHORS.customDomainInput.testId}
                position="inline-end"
              >
                Enter your subdomain to connect it to your funnels.
              </WithGuideHelp>
            )}
            {' '}
            You'll need access to your domain's DNS settings.
            {!suppressGuideAnchors && (
              <>
                <WithGuideHelp guideKey={GUIDE_ANCHORS.customDomainDnsSetup.guideKey} guideService={guideService} data-testid={GUIDE_ANCHORS.customDomainDnsSetup.testId} position="inline-end">
                  <Box component="span" sx={{ width: 0, height: 0, overflow: 'hidden' }} />
                </WithGuideHelp>
                <WithGuideHelp guideKey={GUIDE_ANCHORS.customDomainVerification.guideKey} guideService={guideService} data-testid={GUIDE_ANCHORS.customDomainVerification.testId} position="inline-end">
                  <Box component="span" sx={{ width: 0, height: 0, overflow: 'hidden' }} />
                </WithGuideHelp>
              </>
            )}
          </Typography>

          <TextField
            label="Domain"
            placeholder="promo.yourdomain.com"
            value={domain}
            onChange={handleDomainChange}
            onKeyPress={handleKeyPress}
            error={Boolean(validationError)}
            helperText={
              validationError ||
              'Enter a subdomain like promo.yourdomain.com (not apex domains)'
            }
            fullWidth
            autoFocus
            disabled={submitting}
            slotProps={{
              input: {
                sx: { fontFamily: 'monospace' },
              },
              htmlInput: {
                'data-testid': CUSTOM_DOMAIN_TEST_IDS.domainInput,
              },
            }}
          />

          {apiError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {apiError}
            </Alert>
          )}

          {submitting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Creating DNS authorization...
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Step 2: DNS Instructions */}
      {activeStep === 1 && setupResponse?.dnsRecords && (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Domain <strong>{domain}</strong> has been registered.
            Now add <strong>both</strong> DNS records below.
          </Alert>

          <DNSInstructions
            dnsRecords={setupResponse.dnsRecords}
            showPropagationNote
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            After adding both DNS records, we'll automatically detect the changes
            and continue with SSL certificate setup.
          </Typography>
        </Box>
      )}

      {actionPlacement === 'inline' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={action.onClick}
            disabled={action.disabled}
            data-testid={action['data-testid']}
          >
            {action.label}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DomainSetupForm;
